import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { ArrowLeft, Bell, Trash2, CheckCheck, Settings } from "lucide-react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function Notifications({ user, logout }) {
  const navigate = useNavigate();
  const [notifications, setNotifications] = useState([]);
  const [filter, setFilter] = useState("all"); // all, unread, read
  const [category, setCategory] = useState("all");
  const [academySub, setAcademySub] = useState("all");
  const [loading, setLoading] = useState(true);

  const CATEGORIES = [
    { key: "all", label: "All" },
    { key: "games", label: "Games" },
    { key: "academy", label: "Academy" },
    { key: "membership", label: "Membership" },
    { key: "approvals", label: "Approvals" },
    { key: "announcements", label: "Announcements" },
  ];
  const ACADEMY_SUBS = [
    { key: "all", label: "All" },
    { key: "registration", label: "Registration" },
    { key: "payments", label: "Payments" },
    { key: "sessions", label: "Sessions" },
  ];
  const catOf = (n) => n.category || "other";
  const countByCategory = (key) =>
    notifications.filter((n) => (key === "all" ? true : catOf(n) === key)).length;

  useEffect(() => {
    fetchNotifications();
  }, []);

  const fetchNotifications = async () => {
    try {
      const response = await axios.get(`${API}/notifications/my-notifications`, {
        withCredentials: true
      });
      setNotifications(response.data);
    } catch (error) {
      console.error("Error fetching notifications:", error);
      toast.error("Failed to load notifications");
    } finally {
      setLoading(false);
    }
  };

  const markAsRead = async (notificationId) => {
    try {
      await axios.post(
        `${API}/notifications/mark-read/${notificationId}`,
        {},
        { withCredentials: true }
      );
      
      // Update local state
      setNotifications(prev =>
        prev.map(n => n.id === notificationId ? { ...n, read: true } : n)
      );
    } catch (error) {
      console.error("Error marking as read:", error);
      toast.error("Failed to mark as read");
    }
  };

  const markAllAsRead = async () => {
    try {
      await axios.post(
        `${API}/notifications/mark-all-read`,
        {},
        { withCredentials: true }
      );
      
      // Update local state
      setNotifications(prev => prev.map(n => ({ ...n, read: true })));
      toast.success("All notifications marked as read");
    } catch (error) {
      console.error("Error marking all as read:", error);
      toast.error("Failed to mark all as read");
    }
  };

  const deleteNotification = async (notificationId) => {
    try {
      await axios.delete(`${API}/notifications/${notificationId}`, {
        withCredentials: true
      });
      
      // Update local state
      setNotifications(prev => prev.filter(n => n.id !== notificationId));
      toast.success("Notification deleted");
    } catch (error) {
      console.error("Error deleting notification:", error);
      toast.error("Failed to delete notification");
    }
  };

  const clearAll = async () => {
    if (!window.confirm("Delete all notifications? This cannot be undone.")) {
      return;
    }

    try {
      await axios.delete(`${API}/notifications/clear-all`, {
        withCredentials: true
      });
      
      setNotifications([]);
      toast.success("All notifications deleted");
    } catch (error) {
      console.error("Error clearing notifications:", error);
      toast.error("Failed to clear notifications");
    }
  };

  const handleNotificationClick = (notification) => {
    // Mark as read (check both 'read' and 'is_read' fields)
    if (!notification.read && !notification.is_read) {
      markAsRead(notification.id);
    }
    
    // Determine the target URL - route to unified approvals page
    let targetUrl = null;
    
    if (notification.action_url) {
      targetUrl = notification.action_url;
    } else {
      // Fallback navigation based on notification type
      switch (notification.type) {
        // Academy approvals
        case "academy_registration":
          targetUrl = "/admin/approvals?tab=academy&section=registrations";
          break;
        case "paynow_verification":
          targetUrl = "/admin/approvals?tab=academy&section=payments";
          break;
        case "coach_application":
          targetUrl = "/admin/approvals?tab=academy&section=coaches";
          break;
        // Section approvals
        case "approval_request":
          targetUrl = "/admin/approvals?tab=section&section=members";
          break;
        case "join_request":
          targetUrl = "/admin/approvals?tab=section&section=teams";
          break;
        case "merchant_application":
          targetUrl = "/admin/approvals?tab=section&section=merchants";
          break;
        // Game notifications
        case "game_scheduled":
        case "game_updated":
        case "game_cancelled":
          if (notification.data?.game_id) {
            targetUrl = `/games/${notification.data.game_id}`;
          } else {
            targetUrl = "/games";
          }
          break;
        default:
          // For notifications without action, just mark as read
          toast.info("Notification marked as read");
          return;
      }
    }
    
    // Navigate to target URL
    if (targetUrl) {
      navigate(targetUrl);
    }
  };

  const getNotificationIcon = (type) => {
    switch (type) {
      case "game_scheduled":
        return "⚽";
      case "standby_promoted":
        return "🎉";
      case "join_request_approved":
        return "✅";
      case "game_cancelled":
        return "❌";
      case "game_updated":
        return "📝";
      case "team_announcement":
        return "📢";
      case "event_reminder":
        return "🔔";
      default:
        return "📬";
    }
  };

  const filteredNotifications = notifications.filter(n => {
    const isRead = n.read || n.is_read;
    if (filter === "unread" && isRead) return false;
    if (filter === "read" && !isRead) return false;
    if (category !== "all" && catOf(n) !== category) return false;
    if (category === "academy" && academySub !== "all" && (n.subcategory || "") !== academySub) return false;
    return true;
  });

  const unreadCount = notifications.filter(n => !n.read && !n.is_read).length;

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#0a0f1a]">
        <div className="text-center">
          <div className="animate-spin rounded-full h-16 w-16 border-b-4 border-yellow-500 mx-auto"></div>
          <p className="mt-4 text-slate-300 font-medium">Loading notifications...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0a0f1a] p-4">
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <Button
            onClick={() => navigate("/dashboard")}
            variant="ghost"
            className="text-yellow-500 hover:text-yellow-500"
          >
            <ArrowLeft className="w-5 h-5 mr-2" />
            Back to Dashboard
          </Button>

          <div className="flex items-center gap-2">
            <Button
              onClick={() => navigate("/notification-settings")}
              variant="outline"
              size="sm"
            >
              <Settings className="w-4 h-4 mr-2" />
              Preferences
            </Button>
            {unreadCount > 0 && (
              <Button
                onClick={markAllAsRead}
                variant="outline"
                size="sm"
              >
                <CheckCheck className="w-4 h-4 mr-2" />
                Mark All Read
              </Button>
            )}
            {notifications.length > 0 && (
              <Button
                onClick={clearAll}
                variant="outline"
                size="sm"
                className="text-red-400 hover:text-red-400"
              >
                <Trash2 className="w-4 h-4 mr-2" />
                Clear All
              </Button>
            )}
          </div>
        </div>

        {/* Title and Stats */}
        <div className="mb-6">
          <h1 className="text-3xl font-bold text-white mb-2 flex items-center gap-3">
            <Bell className="w-8 h-8 text-yellow-500" />
            Notifications
          </h1>
          <p className="text-slate-400">
            {unreadCount > 0 ? (
              <span className="font-medium text-yellow-500">
                {unreadCount} unread notification{unreadCount !== 1 ? 's' : ''}
              </span>
            ) : (
              "You're all caught up!"
            )}
          </p>
        </div>

        {/* Category Tabs */}
        <div className="flex flex-wrap gap-2 mb-3" data-testid="notif-category-tabs">
          {CATEGORIES.map((c) => {
            const count = countByCategory(c.key);
            if (c.key !== "all" && count === 0) return null;
            return (
              <Button
                key={c.key}
                onClick={() => { setCategory(c.key); setAcademySub("all"); }}
                variant={category === c.key ? "default" : "outline"}
                size="sm"
                data-testid={`notif-category-${c.key}`}
                className={category === c.key ? "bg-cyan-500/100 hover:bg-cyan-400 text-black" : ""}
              >
                {c.label} ({count})
              </Button>
            );
          })}
        </div>

        {/* Academy sub-tabs */}
        {category === "academy" && (
          <div className="flex flex-wrap gap-2 mb-3 pl-1 border-l-2 border-cyan-500/40" data-testid="notif-academy-subtabs">
            {ACADEMY_SUBS.map((s) => {
              const count = notifications.filter(
                (n) => catOf(n) === "academy" && (s.key === "all" ? true : (n.subcategory || "") === s.key)
              ).length;
              return (
                <Button
                  key={s.key}
                  onClick={() => setAcademySub(s.key)}
                  variant={academySub === s.key ? "default" : "outline"}
                  size="sm"
                  data-testid={`notif-academy-sub-${s.key}`}
                  className={`ml-2 ${academySub === s.key ? "bg-emerald-500/100 hover:bg-emerald-400 text-black" : ""}`}
                >
                  {s.label} ({count})
                </Button>
              );
            })}
          </div>
        )}

        {/* Filter Tabs */}
        <div className="flex gap-2 mb-4">
          <Button
            onClick={() => setFilter("all")}
            variant={filter === "all" ? "default" : "outline"}
            size="sm"
            className={filter === "all" ? "bg-yellow-500/100 hover:bg-yellow-400 text-black" : ""}
          >
            All ({notifications.length})
          </Button>
          <Button
            onClick={() => setFilter("unread")}
            variant={filter === "unread" ? "default" : "outline"}
            size="sm"
            className={filter === "unread" ? "bg-yellow-500/100 hover:bg-yellow-400 text-black" : ""}
          >
            Unread ({unreadCount})
          </Button>
          <Button
            onClick={() => setFilter("read")}
            variant={filter === "read" ? "default" : "outline"}
            size="sm"
            className={filter === "read" ? "bg-yellow-500/100 hover:bg-yellow-400 text-black" : ""}
          >
            Read ({notifications.length - unreadCount})
          </Button>
        </div>

        {/* Notifications List */}
        <div className="space-y-3">
          {filteredNotifications.length === 0 ? (
            <Card className="p-12 text-center bg-slate-900/50 border-2 border-white/5">
              <Bell className="w-16 h-16 text-gray-300 mx-auto mb-4" />
              <h3 className="text-xl font-medium text-slate-400 mb-2">
                {filter === "all" ? "No notifications yet" : `No ${filter} notifications`}
              </h3>
              <p className="text-slate-400">
                {filter === "all"
                  ? "You'll see notifications here when you receive them"
                  : `Switch to another filter to see notifications`}
              </p>
            </Card>
          ) : (
            filteredNotifications.map((notification) => {
              const isRead = notification.read || notification.is_read;
              return (
              <Card
                key={notification.id}
                className={`p-4 cursor-pointer transition-all border-2 ${
                  isRead
                    ? "bg-slate-900/50 border-white/5 hover:border-slate-700"
                    : "bg-yellow-500/10 border-white/5 hover:border-yellow-500/30"
                }`}
                onClick={() => handleNotificationClick(notification)}
              >
                <div className="flex items-start gap-3">
                  <div className="text-2xl flex-shrink-0">
                    {getNotificationIcon(notification.type)}
                  </div>
                  
                  <div className="flex-1 min-w-0">
                    <div className="flex items-start justify-between gap-2 mb-1">
                      <h3 className={`font-medium ${isRead ? 'text-white' : 'text-white font-bold'}`}>
                        {notification.title || notification.type?.replace(/_/g, ' ').toUpperCase()}
                      </h3>
                      {!isRead && (
                        <span className="flex-shrink-0 w-2 h-2 bg-yellow-500/100 rounded-full mt-2"></span>
                      )}
                    </div>
                    
                    <p className="text-slate-400 text-sm mb-2">
                      {notification.message}
                    </p>
                    
                    {notification.action_url && (
                      <p className="text-xs text-blue-400 mb-2">
                        Click to view details →
                      </p>
                    )}
                    
                    <div className="flex items-center justify-between">
                      <span className="text-xs text-slate-400">
                        {new Date(notification.created_at).toLocaleString('en-US', {
                          month: 'short',
                          day: 'numeric',
                          year: 'numeric',
                          hour: '2-digit',
                          minute: '2-digit'
                        })}
                      </span>
                    </div>
                  </div>

                  <Button
                    onClick={(e) => {
                      e.stopPropagation();
                      deleteNotification(notification.id);
                    }}
                    variant="ghost"
                    size="sm"
                    className="text-slate-400 hover:text-red-400 hover:bg-red-500/10"
                  >
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
              </Card>
            )})
          )}
        </div>
      </div>
    </div>
  );
}
